import { Component } from '@angular/core';
import { Router } from '@angular/router';

import { take } from 'rxjs';

import { DialogService, FormNavigationService, GridDataHelpers, SpinnerService } from 'dspx-core';

import { API_ENDPOINT_KEYS } from '../../constants/api-endpoints.constants';
import { APP_DATA_KEYS, NAV_TAB_NUMBERS, PAGE_NUMBERS } from '../../constants/app-data.constants';
import { VERSIONS } from '../../constants/templates.constants';

import { ServerAppSettings } from '../../interfaces/server-app-settings.interface';
import { DashboardResponse } from '../../interfaces/dashboard-response.interface';

import { ServiceWorkerService } from '../../services/service-worker.service';
import { AppDataService } from '../../services/app-data.service';
import { ResponseParserService } from '../../services/response-parser.service';

import { BaseCardSetComponent } from '../base-card-set-component/base-card-set.component';
import { HtmlHelperService } from '../../services/html-helper.service';

@Component({ template: '' })
export abstract class BaseCardSetListComponent extends BaseCardSetComponent {

  formServiceEndpoint: string = API_ENDPOINT_KEYS.getFormConfig;
  versionNo: number = VERSIONS.latest;
  model: any;
  configData: any = null;

  private _hasMoreItemsMessage: string;

  protected templateId: number;
  protected getListApiEndpoint: () => string;
  protected hasFilters: boolean;

  protected constructor(
    protected router: Router,
    protected serviceWorkerService: ServiceWorkerService,
    protected appDataService: AppDataService,
    protected spinnerService: SpinnerService,
    protected htmlHelperService: HtmlHelperService,
    protected dialogService: DialogService,
    protected responseParserService: ResponseParserService,
    protected formNavigationService: FormNavigationService) {

    super(
      router,
      serviceWorkerService,
      appDataService,
      spinnerService,
      htmlHelperService,
      dialogService,
      responseParserService,
      formNavigationService);
  }

  protected initialise(
    templateId: number,
    getListApiEndpoint: () => string,
    hasFilters: boolean): void {

    this.templateId = templateId;
    this.getListApiEndpoint = getListApiEndpoint;
    this.hasFilters = hasFilters;
  }

  /**
   * Loads the grid/search/filter data from the server into the form model.
   * Unlike this.loadGridData, it waits for the next execution cycle (see explanation inside this method).
   * See this.loadGridData for details about the parameters.
   */
  protected loadGridDataOnFormLoad<T>(filterRequest?: T, onGridLoaded?: () => void, currentNavTabNumber?: number, apiEndpoint: string = null): void {
    // This guarantees that the spinner is displayed during the execution of loadGridData, when loadGridData is called from within the onFormLoad event handler.
    // The caller of the onFormLoad event handler calls hideSpinner after the event handler returns.
    // This causes the call to showSpinner inside loadGridData to be useless because it is immediately followed by a call to hideSpinner.
    // To work around the issue, we call loadGridData on the next event cycle, so that the call to showSpinner inside loadGridData happens after call to hideSpinner.
    setTimeout(() => {
      this.loadGridData(filterRequest, onGridLoaded, currentNavTabNumber, apiEndpoint);
    });
  }

  /**
   * Loads the grid/search/filter data from the server into the form model.
   * @param filterRequest Any optional search/filter parameter to be passed to the server API endpoint.
   * @param onGridLoaded An optional call-back that is called after the data from the server has been loaded into the grid.
   * @param currentNavTabNumber When specified, only the slice of model under that NavTab is replaced with the data from the server, otherwise the entire form model is replaced.
   *                            This can be used when the form model contains more data than just one grid/search/filter, to avoid clearing the other data every time.
   * @param apiEndpoint When specified, this endpoint is used to call the server instead of the one returned by this.getListApiEndpoint().
   */
  protected loadGridData<T>(filterRequest?: T, onGridLoaded?: () => void, currentNavTabNumber?: number, apiEndpoint: string = null): void {
    this.spinnerService.showSpinner();
    this.appDataService.getData<DashboardResponse>(apiEndpoint || this.getListApiEndpoint(), filterRequest as Record<string, any>)
      .pipe(take(1))
      .subscribe((data: DashboardResponse) => {
        this.spinnerService.hideSpinner();

        const newModel = data?.response ?? {};

        // NOTE:
        // If currentNavTabNumber is specified, then newModel will only contain data under the currentNavTabNumber NavTab.
        // In this scenario, we don't want to replace this.model with newModel, because it would clear all the other NavTabs.
        // Instead, we simply update the currentNavTabNumber NavTab within this.model.

        if (this.configData) {
          // We apply the conversion of "date/time" columns from string to Date object *before* copying newModel into this.model.
          // This is because in case of partial copy (i.e. when currentNavTabNumber is specified),
          // the other NavTabs will already have the "date/time" columns converted to Date, which would cause the method below to throw an exception.
          GridDataHelpers.ParseDatesInGrids(this.configData, newModel);
        }

        // Update this.model with the data from newModel
        if (currentNavTabNumber !== undefined && currentNavTabNumber !== null && currentNavTabNumber >= 0) {
          // Partial update (only one NavTab)
          const newNavTabModel = this.responseParserService.findNavTabModel(newModel, currentNavTabNumber);
          this.responseParserService.replaceNavTabModel(this.model, currentNavTabNumber, newNavTabModel);
          this.model = { ...this.model };
        }
        else {
          // Full update (all NavTabs)
          this.model = newModel;
        }

        const statusBarContent = data?.hasMoreItems ? this.hasMoreItemsMessage : '';
        this.baseForm.setStatusBar(null, null, statusBarContent);

        onGridLoaded?.();
      });
  }

  protected getDateFromFilter(date): Date {
    if (date) {
      const splitDate = date.split('/');
      return new Date(
        Date.UTC(splitDate[2], splitDate[1] - 1, splitDate[0])
      );
    }
    return null;
  }

  protected get hasMoreItemsMessage(): string {
    if (!this._hasMoreItemsMessage) {
      const maxGridRowCount = this.appDataService.getAppData<ServerAppSettings>(APP_DATA_KEYS.serverAppSettings).maxGridRowCount;
      const extraMessage = this.hasFilters
        ? `Please use the filters to refine your search criteria.`
        : `Only the first ${maxGridRowCount} items are being displayed.`;
      this._hasMoreItemsMessage = `
      <div class="p-grid">
        <div><strong>The search results have exceeded the ${maxGridRowCount} item limit. ${extraMessage}</strong></div>
      </div>
    `;
    }
    return this._hasMoreItemsMessage;
  }

  protected getGridSelectedItems(event: any): any[] {
    const selectedItems = event?.event?.data?.selectedItems;
    return selectedItems
      ?.filter(item => !!item) // Double-clicking on rows results in items containing an empty string being added, which we need to remove.
      ?? [];
  }
}
